/*
 * EILIKO page motion — templates/product.elikotmp.json only.
 * GSAP + ScrollTrigger, native scrolling (no Lenis, no pinning, no scroll hijacking).
 *
 * - Hero copy: short load entrance (never the gallery or the bundle block).
 * - Overview banner: a small scale/settle tied to its entry into view (no pin).
 * - Section heads, cards and photos: short one-shot reveals that start early, so content is
 *   in place by the time it reaches the middle of the screen.
 * - Specs: count-up. Final card: gentle float only while on screen.
 *
 * Safety:
 * - Runs only with the "Enable motion" setting or ?eiliko_motion=1; ?eiliko_smoke=1 only reports.
 * - CSS holds the final static layout; start states are set here and removed with clearProps,
 *   so content stays visible if anything fails.
 * - Only EILIKO elements are animated; Shopify section wrappers are never transformed.
 * - prefers-reduced-motion: nothing is animated.
 */
(function () {
  'use strict';

  if (window.EilikoMotion) return;

  var SEARCH = window.location.search;
  var SMOKE = /[?&]eiliko_smoke=1(&|$)/.test(SEARCH);
  var PREVIEW = /[?&]eiliko_motion=1(&|$)/.test(SEARCH);

  var TARGETS = {
    hero: 'section[id^="MainProduct-"] [data-eiliko-hero]',
    overview: '[data-eik-scene="overview"]',
    interact: '[data-eik-scene="interact"]',
    chat: '[data-eik-scene="chat"]',
    portable: '[data-eik-scene="portable"]',
    style: '[data-eik-scene="style"]',
    specs: '[data-eik-scene="specs"]',
    final: '[data-eik-scene="final"]',
    bundle: '.iqe[data-root]'
  };

  var MEDIA = {
    wide: '(min-width: 990px) and (prefers-reduced-motion: no-preference)',
    narrow: '(max-width: 989px) and (prefers-reduced-motion: no-preference)'
  };

  var CLEAR = 'opacity,transform,visibility';
  var START = 'top 96%';

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  var mm = null;
  var triggerCount = 0;
  var generation = 0;

  function readConfig() {
    var node = document.querySelector('[data-eiliko-motion-config]');
    try {
      return node ? JSON.parse(node.textContent) : {};
    } catch (error) {
      return {};
    }
  }

  function cssLoaded() {
    return getComputedStyle(document.documentElement).getPropertyValue('--eiliko-motion-css').trim() === '1';
  }

  function isOnScreen(el) {
    var rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  }

  function all(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function one(root, selector) {
    return root.querySelector(selector);
  }

  // Short one-shot reveal that removes every inline style it added once finished.
  function reveal(trigger, targets, from, options) {
    targets = (Array.isArray(targets) ? targets : [targets]).filter(Boolean);
    if (!trigger || !targets.length || isOnScreen(trigger)) return;

    options = options || {};
    gsap.fromTo(targets, from, {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      duration: options.duration || 0.4,
      ease: options.ease || 'power2.out',
      stagger: options.stagger || 0,
      delay: options.delay || 0,
      clearProps: CLEAR,
      scrollTrigger: { trigger: trigger, start: options.start || START, once: true }
    });
    triggerCount++;
  }

  // Calls `callback` once the welcome loader is gone and <main> is visible.
  function whenPageVisible(callback) {
    var started = Date.now();
    var main = document.getElementById('MainContent');
    (function check() {
      var loaderGone = !document.documentElement.classList.contains('iqhomes-welcome-active');
      var mainVisible = !main || getComputedStyle(main).visibility !== 'hidden';
      if ((loaderGone && mainVisible) || Date.now() - started > 4000) {
        callback();
      } else {
        requestAnimationFrame(check);
      }
    })();
  }

  /* ---------------------------------------------------------------
     Hero copy (purchase area)
     --------------------------------------------------------------- */
  function buildHero(wide) {
    var product = document.querySelector('section[id^="MainProduct-"]');
    if (!product || !product.querySelector('[data-eiliko-hero]')) return;

    var items = [
      one(product, '.eiliko-hero--title .eiliko-eyebrow'),
      one(product, '.eiliko-hero__name'),
      one(product, '.eiliko-hero__tagline')
    ].concat(all(product, '.eiliko-hero__chips li')).filter(Boolean);

    var entrance = gsap.fromTo(items, { opacity: 0, y: wide ? 14 : 10 }, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      ease: 'power2.out',
      stagger: 0.05,
      paused: true,
      clearProps: CLEAR
    });
    var built = generation;
    whenPageVisible(function () {
      if (built === generation) entrance.play();
    });
    triggerCount++;
  }

  /* ---------------------------------------------------------------
     Overview: banner settles into place as it enters (no pin)
     --------------------------------------------------------------- */
  function buildOverview(scene, wide) {
    var banner = one(scene, '.eik-ov__banner');
    if (!banner) return;
    gsap.fromTo(banner, { scale: wide ? 0.94 : 0.97 }, {
      scale: 1,
      ease: 'none',
      scrollTrigger: { trigger: banner, start: 'top bottom', end: 'top 45%', scrub: 0.4 }
    });
    triggerCount++;
    return function () {
      gsap.set(banner, { clearProps: 'transform' });
    };
  }

  /* ---------------------------------------------------------------
     Grids: cards arrive together with a small stagger
     --------------------------------------------------------------- */
  function buildGrid(scene, selector, wide) {
    var items = all(scene, selector);
    if (!items.length) return;
    reveal(items[0].parentElement, items, { opacity: 0, y: wide ? 16 : 10 }, { stagger: 0.04 });
  }

  function countUp(el) {
    var target = parseFloat(el.dataset.eikCount);
    if (isNaN(target)) return;
    var finalText = el.textContent;
    var decimals = (String(el.dataset.eikCount).split('.')[1] || '').length;
    var state = { value: 0 };
    gsap.to(state, {
      value: target,
      duration: 1,
      ease: 'power2.out',
      scrollTrigger: { trigger: el, start: 'top 92%', once: true },
      onUpdate: function () { el.textContent = state.value.toFixed(decimals); },
      onComplete: function () { el.textContent = finalText; }
    });
    triggerCount++;
  }

  function buildSpecs(scene) {
    var grid = one(scene, '.eik-specs__grid');
    if (!grid || isOnScreen(grid)) return;
    all(scene, '[data-eik-count]').forEach(countUp);
  }

  function buildFinal(scene) {
    all(scene, '.eik-final__bot img').forEach(function (img, i) {
      var loop = gsap.to(img, { y: i ? -6 : -10, duration: i ? 3.2 : 3.8, ease: 'sine.inOut', yoyo: true, repeat: -1, paused: true });
      ScrollTrigger.create({
        trigger: scene,
        start: 'top bottom',
        end: 'bottom top',
        onToggle: function (self) { if (self.isActive) loop.play(); else loop.pause(); }
      });
      triggerCount++;
    });
  }

  // One-shot reveals for every [data-eik-reveal] that is not part of a staggered grid.
  function buildReveals(wide) {
    var grids = '.eik-touch__grid, .eik-carry__gallery, .eik-ov__facts';
    all(document, '.eik [data-eik-reveal]').forEach(function (el) {
      if (el.parentElement && el.parentElement.matches(grids)) return;
      // Reading and navigation stay immediately available; only supporting visuals enter.
      if (el.matches('header, .eik-head, .eik-dress__tabs, .eik-dress__info')) return;
      reveal(el, el, { opacity: 0, y: wide ? 16 : 10 });
    });
  }

  function build(wide, uiOnly) {
    triggerCount = 0;
    generation++;
    var cleanups = [];
    function run(selector, builder) {
      all(document, selector).forEach(function (scene) {
        var cleanup = builder(scene, wide);
        if (typeof cleanup === 'function') cleanups.push(cleanup);
      });
    }

    buildHero(wide);
    // Keep the protected banner's existing lifecycle. A reduced-motion initial load
    // may later enable UI effects, but must not introduce a new banner animation.
    if (!uiOnly) run(TARGETS.overview, buildOverview);
    run(TARGETS.overview, function (scene) { buildGrid(scene, '.eik-ov__fact', wide); });
    run(TARGETS.interact, function (scene) { buildGrid(scene, '.eik-touch__card', wide); });
    run(TARGETS.portable, function (scene) { buildGrid(scene, '.eik-carry__shot', wide); });
    run(TARGETS.specs, buildSpecs);
    run(TARGETS.final, buildFinal);
    buildReveals(wide);

    return function () {
      cleanups.forEach(function (cleanup) { cleanup(); });
    };
  }

  // Lazy images without width/height change section heights after load; re-measure.
  function refreshOnImageLoad() {
    var timer = null;
    function refresh() {
      clearTimeout(timer);
      timer = setTimeout(function () { ScrollTrigger.refresh(); }, 150);
    }
    all(document, '.eik img, section[id^="MainProduct-"] img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', refresh, { once: true });
    });
  }

  function start(uiOnly) {
    mm = gsap.matchMedia();
    mm.add(MEDIA, function (context) {
      return build(Boolean(context.conditions.wide), uiOnly);
    });
    refreshOnImageLoad();
  }

  function restart() {
    if (mm) mm.revert();
    start();
  }

  function motionState(config, librariesReady) {
    if (!(config.enabled || PREVIEW)) return 'disabled';
    if (!librariesReady) return 'libraries missing';
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 'reduced motion (static)';
    return 'running';
  }

  function run() {
    var config = readConfig();
    var report = {
      gsap: gsap ? gsap.version : null,
      scrollTrigger: typeof ScrollTrigger === 'function',
      lenis: typeof window.Lenis === 'function' ? 'loaded (not started)' : 'not loaded',
      css: cssLoaded(),
      targets: {}
    };

    Object.keys(TARGETS).forEach(function (key) {
      report.targets[key] = document.querySelectorAll(TARGETS[key]).length;
    });

    var librariesReady = Boolean(report.gsap && report.scrollTrigger);
    report.ok = Boolean(librariesReady && report.css);
    report.motion = motionState(config, librariesReady);

    if (report.motion === 'running' || report.motion === 'reduced motion (static)') {
      gsap.registerPlugin(ScrollTrigger);
      start(report.motion === 'reduced motion (static)');
      if (config.designMode) {
        ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(function (name) {
          document.addEventListener(name, restart);
        });
      }
    }
    report.triggers = triggerCount;

    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', function () {
      report.motion = motionState(config, librariesReady);
      report.triggers = triggerCount;
    });

    window.EilikoMotion = {
      phase: 4,
      report: report,
      refresh: function () { if (ScrollTrigger) ScrollTrigger.refresh(); },
      destroy: function () { if (mm) mm.revert(); mm = null; }
    };

    if (SMOKE || PREVIEW) {
      console.info('[Eiliko motion] smoke test ' + (report.ok ? 'passed' : 'FAILED') + ' — motion: ' + report.motion, report);
      console.table(report.targets);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
